import { create } from "zustand";

interface HeadlinesFilterState {
  /**
   * Union of every mounted Headlines widget's selections. Derived — write
   * through the setters below, never directly.
   */
  portfoliosFilter: number[];
  portfoliosHighlight: number[];

  /**
   * Per-instance contributions behind the two unions above.
   *
   * A dashboard can carry several Headlines widgets at once, and they used to
   * write the union fields directly: the last writer won, and a widget's unmount
   * cleanup (which writes `[]`) silenced alerts configured by every other
   * Headlines widget still on screen.
   */
  portfoliosFilterByInstance: Record<string, number[]>;
  portfoliosHighlightByInstance: Record<string, number[]>;

  setPortfoliosFilter: (instanceKey: string, ids: number[]) => void;
  setPortfoliosHighlight: (instanceKey: string, ids: number[]) => void;
}

/** Merge one instance's contribution in (or drop it when empty) and re-derive the union. */
const mergeInstance = (
  byInstance: Record<string, number[]>,
  instanceKey: string,
  ids: number[]
): { byInstance: Record<string, number[]>; union: number[] } => {
  const next = { ...byInstance };
  if (ids.length > 0) {
    next[instanceKey] = ids;
  } else {
    delete next[instanceKey];
  }
  return { byInstance: next, union: [...new Set(Object.values(next).flat())] };
};

/**
 * Global store for Headlines filter selections.
 * Used to track portfolio filter/highlight selections for sound alerts.
 */
export const useHeadlinesFilterStore = create<HeadlinesFilterState>((set) => ({
  portfoliosFilter: [],
  portfoliosHighlight: [],
  portfoliosFilterByInstance: {},
  portfoliosHighlightByInstance: {},

  setPortfoliosFilter: (instanceKey, ids) =>
    set((state) => {
      const { byInstance, union } = mergeInstance(
        state.portfoliosFilterByInstance,
        instanceKey,
        ids
      );
      return {
        portfoliosFilterByInstance: byInstance,
        portfoliosFilter: union,
      };
    }),

  setPortfoliosHighlight: (instanceKey, ids) =>
    set((state) => {
      const { byInstance, union } = mergeInstance(
        state.portfoliosHighlightByInstance,
        instanceKey,
        ids
      );
      return {
        portfoliosHighlightByInstance: byInstance,
        portfoliosHighlight: union,
      };
    }),
}));
